import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { adminAPI, ciudadesAPI } from "../../api";
import { imgUrl } from "../../api";
import { formatPrecio, formatFecha, STATUS_COLORES, TIPOS_PROPIEDAD, getLabelTipo } from "../../utils";
import ModalPortal from "../../components/admin/ModalPortal";
import useEsMovil from "../../hooks/useEsMovil";
import toast from "react-hot-toast";
import "./Propiedades.css";

export default function Propiedades() {
  const [propiedades, setPropiedades] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, totalPages: 1 });
  const [cargando, setCargando] = useState(true);
  const [confirmEliminar, setConfirmEliminar] = useState(null);
  const [eliminando, setEliminando] = useState(false);
  const cerrarConfirmacion = useCallback(() => { if (!eliminando) setConfirmEliminar(null); }, [eliminando]);

  const [filtros, setFiltros] = useState({
    search: "", operation: "", type: "", city: "", status: "",
    featured: "", sort: "newest", page: 1,
  });

  const esMovil = useEsMovil();
  const [filtrosAbiertos, setFiltrosAbiertos] = useState(false);

  // Texto del buscador: se actualiza al teclear; la búsqueda se lanza al dejar de escribir
  const [texto, setTexto] = useState("");
  const [ciudades, setCiudades] = useState([]);
  const filtrosRef = useRef(filtros);
  filtrosRef.current = filtros;
  const consultaRef = useRef(0);

  const cargar = useCallback(async (f) => {
    const miConsulta = ++consultaRef.current; // solo vale la respuesta de la última búsqueda
    setCargando(true);
    const params = {};
    Object.entries(f).forEach(([k, v]) => { if (v !== "" && v !== false) params[k] = v; });
    try {
      const res = await adminAPI.listar({ ...params, limit: 15 });
      if (miConsulta !== consultaRef.current) return;
      setPropiedades(res.data.data);
      setPagination(res.data.pagination);
    } catch {
      if (miConsulta === consultaRef.current) toast.error("Error cargando propiedades");
    } finally {
      if (miConsulta === consultaRef.current) setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(filtros); }, []);

  // Ciudades reales dadas de alta en el panel (no una lista fija)
  useEffect(() => {
    ciudadesAPI.listar()
        .then((r) => setCiudades(r.data.data.map((c) => c.nombre)))
        .catch(() => {});
  }, []);

  const aplicarFiltros = (cambios) => {
    const nuevos = { ...filtrosRef.current, ...cambios, page: 1 };
    setFiltros(nuevos);
    cargar(nuevos);
  };

  // Espera 350 ms después de la última tecla antes de buscar
  useEffect(() => {
    const limpio = texto.trim();
    if (limpio === filtrosRef.current.search) return;
    const t = setTimeout(() => aplicarFiltros({ search: limpio }), 350);
    return () => clearTimeout(t);
  }, [texto]);

  const cambiarPagina = (p) => {
    const nuevos = { ...filtros, page: p };
    setFiltros(nuevos);
    cargar(nuevos);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const toggleFeatured = async (prop) => {
    try {
      await adminAPI.patch(prop.id, { featured: !prop.featured });
      setPropiedades((ps) => ps.map((p) => p.id === prop.id ? { ...p, featured: !p.featured } : p));
      toast.success(prop.featured ? "Propiedad desmarcada como destacada" : "Propiedad marcada como destacada");
    } catch { toast.error("Error actualizando"); }
  };

  const cambiarStatus = async (prop, status) => {
    try {
      await adminAPI.patch(prop.id, { status });
      setPropiedades((ps) => ps.map((p) => p.id === prop.id ? { ...p, status } : p));
      toast.success("Status actualizado");
    } catch { toast.error("Error actualizando status"); }
  };

  const eliminar = async (id) => {
    setEliminando(true);
    try {
      await adminAPI.eliminar(id);
      setPropiedades((ps) => ps.filter((p) => p.id !== id));
      setPagination((pag) => ({ ...pag, total: pag.total - 1 }));
      toast.success("Propiedad eliminada");
    } catch { toast.error("Error eliminando"); }
    finally {
      setEliminando(false);
      setConfirmEliminar(null);
    }
  };

  const duplicar = async (id) => {
    try {
      await adminAPI.duplicar(id);
      toast.success("Propiedad duplicada como borrador");
      cargar(filtros);
    } catch { toast.error("Error duplicando"); }
  };

  // Cuántos filtros (además del buscador) están aplicados, para el botón "Filtros (n)"
  const filtrosActivos = ["operation", "type", "city", "status"].filter((k) => filtros[k]).length;

  const limpiar = () => {
    const nuevo = { search: "", operation: "", type: "", city: "", status: "", featured: "", sort: "newest", page: 1 };
    setTexto("");
    setFiltros(nuevo);
    cargar(nuevo);
  };

  return (
      <div className="propiedades-admin fade-in">
        {confirmEliminar && (
            <ModalPortal onClose={cerrarConfirmacion}>
              <div className="modal-overlay" onClick={cerrarConfirmacion}>
                <div className="modal-box card" role="alertdialog" aria-modal="true"
                     aria-labelledby="confirm-eliminar-titulo"
                     onClick={(e) => e.stopPropagation()}>
                  <h3 id="confirm-eliminar-titulo">¿Eliminar propiedad?</h3>
                  <p>Esta acción eliminará permanentemente <strong>"{confirmEliminar.title}"</strong> y todas sus imágenes. No se puede deshacer.</p>
                  <div className="modal-btns">
                    <button className="btn btn-ghost" onClick={cerrarConfirmacion} disabled={eliminando} autoFocus>
                      Cancelar
                    </button>
                    <button className="btn btn-danger" onClick={() => eliminar(confirmEliminar.id)} disabled={eliminando}>
                      {eliminando ? "Eliminando..." : "Sí, eliminar"}
                    </button>
                  </div>
                </div>
              </div>
            </ModalPortal>
        )}

        <div className="page-header">
          <div>
            <h1 className="heading-serif page-title">Propiedades</h1>
            <p style={{ color: "var(--color-text-muted)", fontSize: ".9rem" }}>{pagination.total} propiedades en total</p>
          </div>
          <Link to="/admin/properties/new" className="btn btn-accent">+ Nueva propiedad</Link>
        </div>

        {/* Filtros */}
        <div className="filtros-bar card">
          <input
              type="search"
              className="form-input search-input"
              placeholder="🔍 Buscar por título, clave, ciudad, colonia..."
              aria-label="Buscar propiedades"
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
          />
          {esMovil && (
              <button type="button" className="btn btn-outline btn-sm filtros-toggle"
                      aria-expanded={filtrosAbiertos} aria-controls="filtros-selects"
                      onClick={() => setFiltrosAbiertos((v) => !v)}>
                ⚙️ Filtros{filtrosActivos > 0 ? ` (${filtrosActivos})` : ""} {filtrosAbiertos ? "▲" : "▼"}
              </button>
          )}
          {(!esMovil || filtrosAbiertos) && (
              <div className="filtros-selects" id="filtros-selects">
                <select className="form-select" value={filtros.operation} onChange={(e) => aplicarFiltros({ operation: e.target.value })}>
                  <option value="">Operación</option>
                  <option value="VENTA">Venta</option>
                  <option value="RENTA">Renta</option>
                </select>
                <select className="form-select" value={filtros.type} onChange={(e) => aplicarFiltros({ type: e.target.value })}>
                  <option value="">Tipo</option>
                  {TIPOS_PROPIEDAD.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
                <select className="form-select" value={filtros.city} onChange={(e) => aplicarFiltros({ city: e.target.value })}>
                  <option value="">Ciudad</option>
                  {ciudades.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <select className="form-select" value={filtros.status} onChange={(e) => aplicarFiltros({ status: e.target.value })}>
                  <option value="">Status</option>
                  <option value="ACTIVA">Activa</option>
                  <option value="INACTIVA">Inactiva</option>
                  <option value="RESERVADA">Reservada</option>
                  <option value="VENDIDA">Vendida</option>
                  <option value="RENTADA">Rentada</option>
                </select>
                <button className="btn btn-ghost btn-sm" onClick={limpiar}>✕ Limpiar</button>
              </div>
          )}
        </div>

        {/* Tabla */}
        {cargando ? (
            <div className="spinner" />
        ) : propiedades.length === 0 ? (
            <div className="empty-state card" style={{ padding: "4rem", textAlign: "center" }}>
              <div style={{ fontSize: "2.5rem", marginBottom: "1rem" }}>🏠</div>
              <h3 style={{ marginBottom: ".5rem" }}>No hay propiedades</h3>
              <p style={{ color: "var(--color-text-muted)", marginBottom: "1.5rem" }}>
                {filtros.search || filtros.operation || filtros.type ? "Intenta cambiar los filtros" : "Comienza creando tu primera propiedad"}
              </p>
              {!filtros.search && <Link to="/admin/properties/new" className="btn btn-primary">+ Crear propiedad</Link>}
            </div>
        ) : esMovil ? (
            /* ── Celular: una tarjeta por propiedad, sin scroll horizontal ── */
            <ul className="adm-prop-cards">
              {propiedades.map((p) => {
                const st = STATUS_COLORES[p.status] || {};
                const imgSrc = p.cover_image?.url_thumb;
                return (
                    <li key={p.id} className="adm-prop-card card">
                      <div className="pc-top">
                        <a href={`/propiedades/${p.slug}`} target="_blank" rel="noreferrer"
                           className="pc-thumb prop-thumb-link"
                           aria-label={`Ver "${p.title}" en el portal (abre en pestaña nueva)`}>
                          {imgSrc ? <img src={imgUrl(imgSrc)} alt="" /> : <span>🏠</span>}
                        </a>
                        <div className="pc-info">
                          <p className="pc-titulo">{p.title}</p>
                          <p className="pc-meta">
                            {p.custom_id && <>Ref: {p.custom_id} · </>}{p.city}
                          </p>
                          <p className="pc-precio">{formatPrecio(p.price, p.currency, true)}</p>
                        </div>
                      </div>

                      <div className="pc-estado">
                        <span className={p.operation === "VENTA" ? "badge-venta" : "badge-renta"} style={{ fontSize: ".7rem" }}>
                          {p.operation}
                        </span>
                        <select
                            className="status-select pc-status"
                            style={{ background: st.bg, color: st.color }}
                            value={p.status}
                            aria-label={`Status de ${p.title}`}
                            onChange={(e) => cambiarStatus(p, e.target.value)}
                        >
                          {Object.entries(STATUS_COLORES).map(([v, s]) => (
                              <option key={v} value={v}>{s.label}</option>
                          ))}
                        </select>
                        <button
                            className={`star-btn pc-star ${p.featured ? "starred" : ""}`}
                            onClick={() => toggleFeatured(p)}
                            aria-pressed={!!p.featured}
                            aria-label={p.featured ? "Quitar de destacadas" : "Marcar como destacada"}
                        >
                          ⭐
                        </button>
                      </div>

                      <div className="pc-acciones">
                        <Link to={`/admin/properties/${p.id}/edit`} className="pc-btn">✏️ Editar</Link>
                        <a href={`/propiedades/${p.slug}`} target="_blank" rel="noreferrer" className="pc-btn">👁️ Ver</a>
                        <button type="button" className="pc-btn" onClick={() => duplicar(p.id)}>📋 Duplicar</button>
                        <button type="button" className="pc-btn pc-btn-del" onClick={() => setConfirmEliminar(p)}
                                aria-label={`Eliminar ${p.title}`}>🗑️</button>
                      </div>
                    </li>
                );
              })}
            </ul>
        ) : (
            <div className="table-container card">
              <table className="data-table">
                <thead>
                <tr>
                  <th>Propiedad</th>
                  <th>Tipo / Op.</th>
                  <th>Ciudad</th>
                  <th>Precio</th>
                  <th>Status</th>
                  <th>⭐</th>
                  <th>Creada por</th>
                  <th>Fecha</th>
                  <th>Acciones</th>
                </tr>
                </thead>
                <tbody>
                {propiedades.map((p) => {
                  const st = STATUS_COLORES[p.status] || {};
                  const imgSrc = p.cover_image?.url_thumb;
                  return (
                      <tr key={p.id}>
                        <td>
                          <div className="prop-row">
                            <a
                                href={`/propiedades/${p.slug}`}
                                target="_blank"
                                rel="noreferrer"
                                className="prop-thumb prop-thumb-link"
                                title="Ver en portal"
                                aria-label={`Ver "${p.title}" en el portal (abre en pestaña nueva)`}
                            >
                              {imgSrc ? <img src={imgUrl(imgSrc)} alt="" /> : <span>🏠</span>}
                            </a>
                            <div>
                              <p className="prop-row-title" title={p.title}>{p.title}</p>
                              {p.custom_id && <p className="prop-row-city">Ref: {p.custom_id}</p>}
                            </div>
                          </div>
                        </td>
                        <td>
                          <div style={{ display: "flex", flexDirection: "column", gap: ".3rem" }}>
                        <span className={p.operation === "VENTA" ? "badge-venta" : "badge-renta"} style={{ fontSize: ".7rem" }}>
                          {p.operation}
                        </span>
                            <span style={{ textAlign: "center", fontSize: ".75rem", color: "var(--color-text-muted)" }}>
                          {getLabelTipo(p.type)}
                        </span>
                          </div>
                        </td>
                        <td style={{ fontSize: ".875rem" }}>{p.city}</td>
                        <td className="td-price" style={{ fontSize: ".85rem" }}>
                          {formatPrecio(p.price, p.currency, true)}
                        </td>
                        <td>
                          <select
                              className="status-select"
                              style={{ background: st.bg, color: st.color }}
                              value={p.status}
                              onChange={(e) => cambiarStatus(p, e.target.value)}
                          >
                            {Object.entries(STATUS_COLORES).map(([v, s]) => (
                                <option key={v} value={v}>{s.label}</option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <button
                              className={`star-btn ${p.featured ? "starred" : ""}`}
                              onClick={() => toggleFeatured(p)}
                              title={p.featured ? "Quitar de destacadas" : "Marcar como destacada"}
                          >
                            ⭐
                          </button>
                        </td>
                        {/* Columna Creada por */}
                        <td>
                          {p.created_by_username ? (
                              <div className="creado-por">
                                <div className="creado-avatar">
                                  {p.created_by_name?.charAt(0).toUpperCase()}
                                </div>
                                <div>
                                  <p style={{ fontSize: ".8rem", fontWeight: 600 }}>{p.created_by_name}</p>
                                  <p style={{ fontSize: ".72rem", color: "var(--color-text-muted)" }}>@{p.created_by_username}</p>
                                </div>
                              </div>
                          ) : (
                              <span style={{ fontSize: ".8rem", color: "var(--color-text-muted)" }}>—</span>
                          )}
                        </td>
                        <td style={{ fontSize: ".8rem", color: "var(--color-text-muted)" }}>
                          {formatFecha(p.created_at)}
                        </td>
                        <td>
                          <div className="action-btns">
                            <Link to={`/admin/properties/${p.id}/edit`} className="btn btn-ghost btn-sm" title="Editar">✏️</Link>
                            <a href={`/propiedades/${p.slug}`} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm" title="Ver en portal">👁️</a>
                            <button className="btn btn-ghost btn-sm" title="Duplicar" onClick={() => duplicar(p.id)}>📋</button>
                            <button className="btn btn-ghost btn-sm" title="Eliminar" onClick={() => setConfirmEliminar(p)} style={{ color: "var(--color-error)" }}>🗑️</button>
                          </div>
                        </td>
                      </tr>
                  );
                })}
                </tbody>
              </table>
            </div>
        )}

        {!cargando && pagination.totalPages > 1 && (
            <div className="paginacion" style={{ display: "flex", justifyContent: "center", gap: ".4rem", marginTop: "1.5rem" }}>
              <button className="btn btn-outline btn-sm" disabled={filtros.page <= 1} onClick={() => cambiarPagina(filtros.page - 1)}>← Anterior</button>
              {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((p) => (
                  <button key={p} className={`btn btn-sm ${filtros.page === p ? "btn-primary" : "btn-ghost"}`} onClick={() => cambiarPagina(p)}>{p}</button>
              ))}
              <button className="btn btn-outline btn-sm" disabled={filtros.page >= pagination.totalPages} onClick={() => cambiarPagina(filtros.page + 1)}>Siguiente →</button>
            </div>
        )}
      </div>
  );
}